import * as React from "react";
import { observer, inject } from "mobx-react";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { LocationProps } from "./typings";
import { LocationField } from "../LocationField/LocationField";
import {
  FlightWizardState,
  PackageWizardState,
  LOBState,
  LxWizardState,
  CarWizardState,
  FlightType,
} from "stores/wizard/state";
import { SwapLocations } from "../SwapLocations/SwapLocations";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { useLocalization } from "@shared-ui/localization-context";
import { ACTION_DONE, getTracker } from "utils/TrackingUtils";
import { DefaultLocation } from "./components/DefaultLocation";
import { LocationList } from "./components/LocationList";
import { LocationType } from "./components/LocationType";

enum LocationFieldType {
  STOPOVER_DEFAULT_LOCATION = "stopoverDefaultLocation",
  STOPOVER_LOCATION_LIST = "stopoverLocationList",
  ORIGIN_DEFAULT_LOCATION = "originDefaultLocation",
  ORIGIN_LOCATION_LIST = "originLocationList",
  DESTINATION_DEFAULT_LOCATION = "destinationDefaultLocation",
  DESTINATION_LOCATION_LIST = "destinationLocationList",
  STANDARD = "standard",
}

export const Location: React.FunctionComponent<LocationProps> = inject(
  "context",
  "routeStore",
  "analytics",
  "experiment",
  "page"
)(
  observer(
    ({
      analytics,
      colSpan,
      context,
      destinationField,
      experiment,
      globalConfig,
      lobState,
      multiCityFlight,
      multiLegIndex,
      onClick,
      onewayFlight,
      originField,
      routeStore,
      setInputsRefs,
      showSwapLocationsControl,
      page,
      stopoverOrigin,
      stopoverDestination,
      stopoverMultipleOrigin,
      stopoverMultipleDestination,
    }) => {
      if (!context || !routeStore) {
        // Check for injected stores
        return null;
      }

      const { DESTINATION, ORIGIN } = LocationType;
      const useLocationConfiguration = (index: number | undefined, state: LOBState) => {
        if (multiCityFlight && state.config.multiCityFlight.location) {
          return state.config.multiCityFlight.location; // multicity first leg
        }

        if (
          index !== undefined &&
          state.config.multiCityFlight &&
          (state instanceof FlightWizardState || state instanceof PackageWizardState)
        ) {
          return state.config.multiCityFlight.legs[index].location; // multicity additional legs
        }

        if (state instanceof PackageWizardState && onewayFlight && state.config.onewayFlight?.location) {
          return state.config.onewayFlight.location; // oneway flight
        }

        if (state instanceof LxWizardState) {
          return state.config.location;
        }

        return state.tabSelected === false
          ? state.subLOBState === "toAirport"
            ? state.config.gtConfig.toAirportLocation
            : state.config.gtConfig.location
          : state.config.location;
      };

      const locationConfig = useLocationConfiguration(multiLegIndex, lobState);
      const locationState =
        multiLegIndex !== undefined &&
        (lobState instanceof FlightWizardState || lobState instanceof PackageWizardState) &&
        lobState.listOfAdditionalLegs
          ? lobState.listOfAdditionalLegs[multiLegIndex].location
          : lobState.location;

      const { formatText } = useLocalization();

      // TODO: Consolidate functions doing related logic.

      const onOriginLocationFieldUpdate = (selection: TypeaheadSelection) => {
        if (lobState.updateOriginSelection) {
          lobState.updateOriginSelection(selection, multiLegIndex);
        }

        tracker.trackEvent(_origin + ACTION_DONE);
      };

      const onDestinationLocationFieldUpdate = (selection: TypeaheadSelection) => {
        lobState.updateDestinationSelection(selection, multiLegIndex, experiment, context, page);

        tracker.trackEvent(_destination + ACTION_DONE);
      };

      const getDestinationLabelToken = () => {
        const { withValueLabelToken, noValueLabelToken, labelToken } = locationConfig.destination;

        return lobState instanceof CarWizardState && lobState.isDesktop
          ? destinationValue
            ? withValueLabelToken
            : noValueLabelToken
          : labelToken;
      };

      const swapOriginAndDestination = () => {
        if (lobState.swapLocations) {
          lobState.swapLocations(multiLegIndex);
        }
      };

      const track = (event: string) => {
        tracker.trackEvent(event);
      };

      const originInvalidMessage = lobState.originInvalidKey ? formatText(lobState.originInvalidKey) : "";
      const destinationInvalidMessage = lobState.destinationInvalidKey
        ? formatText(lobState.destinationInvalidKey)
        : "";

      const locationClassWithSwap = showSwapLocationsControl ? "locationWithSwap" : "";
      const locationFieldCols = originField && destinationField ? 1 : 2;

      const tracker = getTracker(lobState.trackingEnabled, analytics, lobState.moduleName);
      const destinationValue = locationState.destination.value;

      const _origin = "origin.";
      const _destination = "destination.";
      const destinationLabelToken = getDestinationLabelToken();

      const { defaultOrigin, defaultMultipleOrigin, defaultDestination, defaultMultipleDestination } =
        globalConfig || {};

      const defaultDestinationValue = lobState instanceof CarWizardState ? "" : defaultDestination;
      const defaultOriginValue = lobState instanceof CarWizardState ? defaultDestination : defaultOrigin;
      const defaultMultipleDestinationValue = lobState instanceof CarWizardState ? "" : defaultMultipleDestination;
      const defaultMultipleOriginValue =
        lobState instanceof CarWizardState ? defaultMultipleDestination : defaultMultipleOrigin;

      const buildMockTypeaheadSelection = (term: string): TypeaheadSelection => {
        const typeaheadMockResponse = {
          data: {
            selected: "",
            regionId: "",
            originId: "",
            cityId: "",
            type: "",
            hierarchyInfo: {
              airport: {
                airportCode: "",
                airportId: "",
                metrocode: "",
                multicity: "",
              },
              country: {
                name: "",
                isoCode2: "",
                isoCode3: "",
              },
            },
            location: {
              lat: "",
              long: "",
            },
            regionNames: {
              shortName: "",
            },
          },
          term,
        };

        return typeaheadMockResponse;
      };

      const getOriginFieldType = () => {
        if (stopoverOrigin && lobState.subLOBState === FlightType.MULTI_CITY)
          return LocationFieldType.STOPOVER_DEFAULT_LOCATION;
        if (stopoverMultipleOrigin && lobState.subLOBState === FlightType.MULTI_CITY)
          return LocationFieldType.STOPOVER_LOCATION_LIST;
        if (defaultOriginValue && lobState.subLOBState !== FlightType.MULTI_CITY)
          return LocationFieldType.ORIGIN_DEFAULT_LOCATION;
        if (defaultMultipleOriginValue && lobState.subLOBState !== FlightType.MULTI_CITY)
          return LocationFieldType.ORIGIN_LOCATION_LIST;
        return LocationFieldType.STANDARD;
      };

      const getDestinationFieldType = () => {
        if (stopoverDestination && lobState.subLOBState === FlightType.MULTI_CITY)
          return LocationFieldType.STOPOVER_DEFAULT_LOCATION;
        if (stopoverMultipleDestination && lobState.subLOBState === FlightType.MULTI_CITY)
          return LocationFieldType.STOPOVER_LOCATION_LIST;
        if (defaultDestinationValue && lobState.subLOBState !== FlightType.MULTI_CITY)
          return LocationFieldType.DESTINATION_DEFAULT_LOCATION;
        if (defaultMultipleDestinationValue && lobState.subLOBState !== FlightType.MULTI_CITY)
          return LocationFieldType.DESTINATION_LOCATION_LIST;
        return LocationFieldType.STANDARD;
      };

      const renderOrigin = () => {
        const fieldType = getOriginFieldType();
        switch (fieldType) {
          case LocationFieldType.STOPOVER_DEFAULT_LOCATION:
            return (
              <DefaultLocation
                name={
                  lobState instanceof FlightWizardState ||
                  (lobState instanceof PackageWizardState && lobState.isMulticityFlight())
                    ? ""
                    : locationConfig.origin.inputName
                }
                lobState={lobState}
                defaultLocation={stopoverOrigin || ""}
                locationType={ORIGIN}
                locationConfig={locationConfig}
                biasedStopover={stopoverOrigin}
              />
            );
          case LocationFieldType.STOPOVER_LOCATION_LIST:
            const onBiasedSelectChange = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
              if (lobState.updateOriginSelection) {
                lobState.updateOriginSelection(
                  buildMockTypeaheadSelection(selectEvent.currentTarget.value),
                  multiLegIndex
                );
              }
            };

            return (
              <LocationList
                name={
                  lobState instanceof FlightWizardState ||
                  (lobState instanceof PackageWizardState && lobState.isMulticityFlight())
                    ? ""
                    : locationConfig.origin.inputName
                }
                lobState={lobState}
                tracker={tracker}
                locationList={stopoverMultipleOrigin || []}
                locationType={ORIGIN}
                locationConfig={locationConfig}
                biasedStopover={stopoverMultipleOrigin}
                onBiasedSelectChange={onBiasedSelectChange}
                multiLegIndex={multiLegIndex}
              />
            );

          case LocationFieldType.ORIGIN_DEFAULT_LOCATION:
            return (
              <DefaultLocation
                name={
                  lobState instanceof FlightWizardState ||
                  (lobState instanceof PackageWizardState && lobState.isMulticityFlight())
                    ? ""
                    : locationConfig.origin.inputName
                }
                lobState={lobState}
                defaultLocation={defaultOriginValue || ""}
                locationType={ORIGIN}
                locationConfig={locationConfig}
              />
            );

          case LocationFieldType.ORIGIN_LOCATION_LIST:
            return (
              <LocationList
                name={
                  lobState instanceof FlightWizardState ||
                  (lobState instanceof PackageWizardState && lobState.isMulticityFlight())
                    ? ""
                    : locationConfig.origin.inputName
                }
                lobState={lobState}
                tracker={tracker}
                locationList={defaultMultipleOriginValue || []}
                locationType={ORIGIN}
                locationConfig={locationConfig}
                onBiasedSelectChange={undefined}
                multiLegIndex={multiLegIndex}
              />
            );

          case LocationFieldType.STANDARD:
            return (
              <LocationField
                setInputsRefs={setInputsRefs}
                context={context}
                onUpdate={onOriginLocationFieldUpdate}
                icon={locationConfig.origin.icon}
                name={
                  lobState instanceof FlightWizardState ||
                  (lobState instanceof PackageWizardState && lobState.isMulticityFlight())
                    ? ""
                    : locationConfig.origin.inputName
                }
                label={formatText(locationConfig.origin.labelToken)}
                typeaheadPlaceholder={formatText(locationConfig.origin.typeaheadLabelToken)}
                typeaheadEmptyResultPlaceholder={formatText(locationConfig.origin.typeaheadEmptyResultPlaceholder)}
                invalid={originInvalidMessage}
                value={locationState.origin.value}
                essAdapterConfig={locationConfig.origin.essAdapterConfig}
                stid={`location-field-${locationConfig.origin.inputName}`}
                config={locationConfig.origin}
                location={locationState.origin}
                track={track}
                trackId={_origin}
                globalConfig={globalConfig}
              />
            );

          default:
            return null;
        }
      };

      const renderDestination = () => {
        const fieldType = getDestinationFieldType();

        switch (fieldType) {
          case LocationFieldType.STOPOVER_DEFAULT_LOCATION:
            return (
              <DefaultLocation
                name={
                  lobState instanceof FlightWizardState ||
                  (lobState instanceof PackageWizardState && lobState.isMulticityFlight())
                    ? ""
                    : locationConfig.destination.inputName
                }
                lobState={lobState}
                defaultLocation={stopoverDestination || ""}
                locationType={DESTINATION}
                locationConfig={locationConfig}
                biasedStopover={stopoverDestination}
              />
            );
          case LocationFieldType.STOPOVER_LOCATION_LIST:
            const onBiasedSelectChange = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
              if (lobState.updateDestinationSelection) {
                lobState.updateDestinationSelection(
                  buildMockTypeaheadSelection(selectEvent.currentTarget.value),
                  undefined
                );
              }
            };

            return (
              <LocationList
                name={
                  lobState instanceof FlightWizardState ||
                  (lobState instanceof PackageWizardState && lobState.isMulticityFlight())
                    ? ""
                    : locationConfig.destination.inputName
                }
                lobState={lobState}
                tracker={tracker}
                locationList={stopoverMultipleDestination || []}
                locationType={DESTINATION}
                locationConfig={locationConfig}
                biasedStopover={stopoverMultipleDestination}
                onBiasedSelectChange={onBiasedSelectChange}
              />
            );

          case LocationFieldType.DESTINATION_DEFAULT_LOCATION:
            return (
              <DefaultLocation
                name={
                  lobState instanceof FlightWizardState ||
                  (lobState instanceof PackageWizardState && lobState.isMulticityFlight())
                    ? ""
                    : locationConfig.destination.inputName
                }
                lobState={lobState}
                defaultLocation={defaultDestinationValue || ""}
                locationType={DESTINATION}
                locationConfig={locationConfig}
                biasedStopover={stopoverDestination}
              />
            );

          case LocationFieldType.DESTINATION_LOCATION_LIST:
            const onDestinationSelectChange =
              lobState.subLOBState === FlightType.MULTI_CITY
                ? (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
                    if (lobState.updateDestinationSelection) {
                      lobState.updateDestinationSelection(
                        buildMockTypeaheadSelection(selectEvent.currentTarget.value),
                        0
                      );
                    }
                  }
                : undefined;

            return (
              <LocationList
                name={
                  lobState instanceof FlightWizardState ||
                  (lobState instanceof PackageWizardState && lobState.isMulticityFlight())
                    ? ""
                    : locationConfig.destination.inputName
                }
                lobState={lobState}
                tracker={tracker}
                locationList={defaultMultipleDestinationValue || []}
                locationType={DESTINATION}
                locationConfig={locationConfig}
                onBiasedSelectChange={onDestinationSelectChange}
              />
            );

          case LocationFieldType.STANDARD:
            return (
              <LocationField
                config={locationConfig.destination}
                context={context}
                essAdapterConfig={locationConfig.destination.essAdapterConfig}
                icon={locationConfig.destination.icon}
                invalid={destinationInvalidMessage}
                label={formatText(destinationLabelToken)}
                location={locationState.destination}
                name={
                  lobState instanceof FlightWizardState ||
                  (lobState instanceof PackageWizardState && lobState.isMulticityFlight())
                    ? ""
                    : locationConfig.destination.inputName
                }
                onClick={onClick}
                onUpdate={onDestinationLocationFieldUpdate}
                setInputsRefs={setInputsRefs}
                stid={`location-field-${locationConfig.destination.inputName}`}
                track={track}
                trackId={_destination}
                typeaheadPlaceholder={formatText(locationConfig.destination.typeaheadLabelToken)}
                typeaheadEmptyResultPlaceholder={formatText(locationConfig.destination.typeaheadEmptyResultPlaceholder)}
                value={destinationValue}
              />
            );

          default:
            return null;
        }
      };

      return (
        <UitkLayoutGridItem colSpan={colSpan}>
          <div>
            <UitkLayoutGrid
              columns={{ small: 1, medium: 2 }}
              space="three"
              className={`Location ${locationClassWithSwap}`}
            >
              {originField && locationConfig.origin && (
                <UitkLayoutGridItem colSpan={{ small: 1, medium: locationFieldCols }}>
                  <div>{renderOrigin()}</div>
                </UitkLayoutGridItem>
              )}
              {showSwapLocationsControl && <SwapLocations swapLocations={swapOriginAndDestination} />}
              {destinationField && locationConfig.destination && (
                <UitkLayoutGridItem colSpan={{ small: 1, medium: locationFieldCols }}>
                  <div>{renderDestination()}</div>
                </UitkLayoutGridItem>
              )}
            </UitkLayoutGrid>
          </div>
        </UitkLayoutGridItem>
      );
    }
  )
);
